'use client'

import { useEffect, useRef } from 'react'
import ReactHowler from 'react-howler'
import { useAudioStore } from '@/lib/store/audio-store'

/**
 * Global audio player component using react-howler
 * This component should be mounted once at the app root level
 */
export default function AudioPlayer() {
  const playerRef = useRef<ReactHowler | null>(null)
  const rafIdRef = useRef<number | null>(null)

  const currentSrc = useAudioStore((state) => state.currentSrc)
  const isPlaying = useAudioStore((state) => state.isPlaying)
  const muted = useAudioStore((state) => state.muted)
  const loop = useAudioStore((state) => state.loop)
  const rate = useAudioStore((state) => state.rate)
  const markEnded = useAudioStore((state) => state.markEnded)
  const seekVersion = useAudioStore((state) => state.seekVersion)
  const setIsPlaying = useAudioStore((state) => state.setIsPlaying)
  const setIsLoading = useAudioStore((state) => state.setIsLoading)
  const setCurrentTime = useAudioStore((state) => state.setCurrentTime)
  const setDuration = useAudioStore((state) => state.setDuration)

  useEffect(() => {
    // ReactHowler only reads the rate prop when it creates the Howl.
    playerRef.current?.howler?.rate(rate)
  }, [rate, currentSrc])

  // Handle seeking
  useEffect(() => {
    const player = playerRef.current
    if (!player || !currentSrc || !player.howler) {
      return
    }

    const currentTime = useAudioStore.getState().currentTime
    const currentSeek = player.seek() || 0
    if (Math.abs(currentSeek - currentTime) > 0.001) {
      player.howler.seek(currentTime)
    }
  }, [seekVersion, currentSrc])

  // Setup RAF loop for time updates
  const startTimeUpdateLoop = () => {
    stopTimeUpdateLoop()

    const updateTime = () => {
      const player = playerRef.current
      const state = useAudioStore.getState()
      if (player && player.howler && state.isPlaying && state.currentSrc === currentSrc) {
        const seek = player.howler.seek() || 0
        setCurrentTime(seek)
        rafIdRef.current = requestAnimationFrame(updateTime)
      }
    }

    rafIdRef.current = requestAnimationFrame(updateTime)
  }

  const stopTimeUpdateLoop = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimeUpdateLoop()
    }
  }, [])

  if (!currentSrc) {
    return null
  }

  return (
    <ReactHowler
      ref={playerRef}
      src={currentSrc}
      playing={isPlaying}
      html5={true}
      loop={loop}
      rate={rate}
      mute={muted}
      onLoad={() => {
        if (useAudioStore.getState().currentSrc !== currentSrc) return
        setIsLoading(false)
        const player = playerRef.current
        if (player && player.howler) {
          setDuration(player.howler.duration())
          // A paused source may finish loading after the initial seek effect ran.
          const position = useAudioStore.getState().currentTime
          if (position > 0) player.howler.seek(position)
        }
      }}
      onPlay={() => {
        if (useAudioStore.getState().currentSrc !== currentSrc) return
        startTimeUpdateLoop()
      }}
      onPause={() => {
        // Howler can deliver a queued pause after the next source has started.
        if (useAudioStore.getState().currentSrc !== currentSrc || playerRef.current?.howler?.playing()) return
        setIsPlaying(false)
        stopTimeUpdateLoop()
      }}
      onStop={() => {
        if (useAudioStore.getState().currentSrc !== currentSrc) return
        setIsPlaying(false)
        setCurrentTime(0)
        stopTimeUpdateLoop()
      }}
      onEnd={() => {
        const state = useAudioStore.getState()
        if (state.currentSrc !== currentSrc || state.loop) return
        setIsPlaying(false)
        setCurrentTime(0)
        stopTimeUpdateLoop()
        markEnded()
      }}
      onLoadError={(_id, error) => {
        if (useAudioStore.getState().currentSrc !== currentSrc) return
        console.error('Audio playback error:', error)
        setIsPlaying(false)
        setIsLoading(false)
        stopTimeUpdateLoop()
      }}
    />
  )
}
